/**
 * `/textbook` — the real import lifecycle (#144, sub-issue of #76, Track C1;
 * BACKEND_DESIGN §5.2, §3, §4.2).
 *
 * `GET /imports` is the only source of truth this screen reads:
 * `lifecycleState` is the value `import_runs.lifecycle_state` actually holds,
 * never something inferred here from whether a page or a hold happens to
 * exist. A view that derived a state from what data happens to exist would
 * be a conclusion wearing a status — exactly the failure this issue exists to
 * avoid.
 *
 * **No percentage, anywhere, including in a tooltip or an aria label**
 * (CLAUDE.md's hard rule: "No percentage, anywhere, ever"). Progress through
 * the pipeline is a named state plus a count of pages, never a bar with a
 * number on it — issue #144's own framing of that rule, not a CLAUDE.md
 * quotation (corrected in the #144 fix pass, invariant audit) — and
 * `textbook-page.test.tsx` asserts the rendered screen contains no `%`.
 * `pageCount` and `heldPageCount` are shown as two independent counts, never
 * combined into a fraction of one another.
 */

import { useQuery } from '@tanstack/react-query';
import type { ImportRunsResponse } from '@davinci/contracts';
import { ApiRequestError } from '../../api/client.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { queryKeys } from '../../api/query-keys.js';
import { importFailureKindLabel, importLifecycleSentence } from '../../i18n/import-lifecycle-labels.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

const messages = defineMessages({
  en: {
    heading: 'Textbook',
    loading: 'Loading the import status…',
    operatorOnly: 'The textbook is the operator’s tool; it isn’t part of your account.',
    empty: 'No source has been imported yet.',
    pages: (n: number) => (n === 1 ? '1 page recorded.' : `${String(n)} pages recorded.`),
    held: (n: number) => {
      if (n === 0) return 'No pages are currently held.';
      return n === 1 ? '1 page is held pending review.' : `${String(n)} pages are held pending review.`;
    },
  },
  he: {
    heading: 'ספר הלימוד',
    loading: 'טוען את מצב הייבוא…',
    operatorOnly: 'ספר הלימוד הוא כלי של מפעילי המערכת, והוא אינו חלק מהחשבון שלכם.',
    empty: 'עוד לא יובא אף מקור.',
    pages: (n: number) => (n === 1 ? 'נרשם עמוד אחד.' : `נרשמו ${String(n)} עמודים.`),
    held: (n: number) => {
      if (n === 0) return 'אין כרגע עמודים בהמתנה.';
      return n === 1 ? 'עמוד אחד ממתין לבדיקה.' : `${String(n)} עמודים ממתינים לבדיקה.`;
    },
  },
});

type ImportRun = ImportRunsResponse['runs'][number];

interface SourceGroup {
  readonly sourceId: string;
  readonly sourceTitle: string;
  readonly runs: readonly ImportRun[];
}

/**
 * Runs, grouped under the source they belong to — `GET /imports` already
 * orders runs newest first, and grouping preserves that: a source's group
 * appears where its first (i.e. newest) run does, and every run for that
 * source is collected into it wherever else it appears in the list.
 */
function groupBySource(runs: readonly ImportRun[]): readonly SourceGroup[] {
  const order: string[] = [];
  const bySourceId = new Map<string, { sourceTitle: string; runs: ImportRun[] }>();
  for (const run of runs) {
    const existing = bySourceId.get(run.sourceId);
    if (existing === undefined) {
      bySourceId.set(run.sourceId, { sourceTitle: run.sourceTitle, runs: [run] });
      order.push(run.sourceId);
    } else {
      existing.runs.push(run);
    }
  }
  return order.map((sourceId) => {
    const group = bySourceId.get(sourceId);
    if (group === undefined) {
      // Unreachable: every id in `order` was inserted into the map above in
      // the same pass, and nothing removes entries from it afterward.
      throw new Error(`unreachable: ${sourceId} missing from its own group`);
    }
    return { sourceId, sourceTitle: group.sourceTitle, runs: group.runs };
  });
}

function ImportRunItem({ run }: { readonly run: ImportRun }): React.JSX.Element {
  const t = useMessages(messages);
  const { locale } = useLocale();
  return (
    <li>
      <p>{importLifecycleSentence(run.lifecycleState, locale)}</p>
      <p>{t.pages(run.pageCount)}</p>
      <p>{t.held(run.heldPageCount)}</p>
      {run.lifecycleState === 'failed' && run.failureKind !== null ? (
        <p>
          {importFailureKindLabel(run.failureKind, locale)}
          {run.failureDetail !== null ? ` ${run.failureDetail}` : null}
        </p>
      ) : null}
    </li>
  );
}

export function TextbookPage(): React.JSX.Element {
  const api = useApi();
  const t = useMessages(messages);
  const imports = useQuery({
    queryKey: queryKeys.imports,
    queryFn: () => api.getImports(),
  });

  return (
    <div className="page">
      <h1>{t.heading}</h1>

      {imports.isPending ? <p aria-live="polite">{t.loading}</p> : null}

      {imports.isError && imports.error instanceof ApiRequestError && imports.error.code === 'founder_only' ? (
        <p>{t.operatorOnly}</p>
      ) : imports.isError ? (
        <FailureNotice error={imports.error} onRetry={() => void imports.refetch()} />
      ) : null}

      {imports.isSuccess ? (
        imports.data.runs.length === 0 ? (
          <p>{t.empty}</p>
        ) : (
          groupBySource(imports.data.runs).map((group) => (
            <section key={group.sourceId}>
              <h2 dir="auto">{group.sourceTitle}</h2>
              <ul>
                {group.runs.map((run) => (
                  <ImportRunItem key={run.importRunId} run={run} />
                ))}
              </ul>
            </section>
          ))
        )
      ) : null}
    </div>
  );
}
